import { Subject } from "rxjs";
import { clamp, stepMap } from "@zyzgroup/core-common";
import { ShapeCombo, ShapeComboOptions } from "../Shape";
import BallShape from "./BallShape";
import RectShape from "./RectShape";
import { PathD } from "../line";

export type SliderShapeComboOptions = {
  sliderMin?: number;
  sliderMax?: number;
  sliderStep?: number;
  sliderDefaultValue?: number;
  sliderWidth?: number;
  sliderHeight?: number;
  sliderBackgroundColor?: string;
  sliderForegroundColor?: string;
} & ShapeComboOptions;

export class SliderShapeCombo extends ShapeCombo {
  protected options?: SliderShapeComboOptions;
  public value$: Subject<number> = new Subject<number>();
  public subscription: any;

  constructor(options?: SliderShapeComboOptions) {
    super(options);
    this.options = options;

    const min = options?.sliderMin || 0;
    const max = options?.sliderMax || 100;
    const step = options?.sliderStep || 1;

    const stepFunc = stepMap(min, max, step);

    const value = stepFunc(
      typeof options?.sliderDefaultValue == "number"
        ? clamp(options.sliderDefaultValue, min, max)
        : min
    );
    this.value$.next(value);

    const percent = value / (max - min);

    const rectWidth = options?.sliderWidth || 100;
    const rectHeight = options?.sliderHeight || 4;
    const rectBorderRadius = rectHeight * 0.5;
    const ballRadius = rectHeight * 2.5;

    const backgroundColor = options?.sliderBackgroundColor || "#f5f5f5";
    const foregroundColor = options?.sliderForegroundColor || "#5a82f0";

    const backgroundRect = new RectShape({
      rectWidth,
      rectHeight,
      rectBorderRadius,
      attrs: {
        fill: backgroundColor
      },
      drag: {
        enable: false
      }
    });
    this.addShape(backgroundRect);

    const occupyRect = new RectShape({
      rectWidth: rectWidth * percent,
      rectHeight: rectHeight,
      rectBorderRadius,
      attrs: {
        fill: foregroundColor
      },
      drag: {
        enable: false
      }
    });
    this.addShape(occupyRect);

    const handle = new BallShape({
      x: rectWidth * percent,
      y: rectBorderRadius,
      ballRadius,
      attrs: {
        fill: foregroundColor
      },
      drag: {
        enable: true,
        alongPath: new PathD([
          ["m", 0, 0],
          ["l", rectWidth, 0]
        ])
      }
    });
    this.addShape(handle);

    this.subscription = handle.transform$.subscribe(({ dX }) => {
      console.log(dX);
      const percent = (handle.x - backgroundRect.x) / rectWidth;
      occupyRect.update({
        rectWidth: rectWidth * percent
      });
      this.value$.next(stepFunc(percent * (max - min) + min));
    });
  }

  public destroy() {
    super.destroy();
    this.subscription.unsubscribe();
  }

  protected cloneShapeCombo(): SliderShapeCombo {
    const shapeCombo = new SliderShapeCombo({
      ...super.getBaseOptions(),
      ...this.options
    });
    return shapeCombo;
  }
}

export default SliderShapeCombo;
